import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { LocalStorageService } from '../../../@core/services/local-storage.service';
import { ClientsApiActions } from '../../../@store/clients/clients.actions';
import {
  selectClientError,
  selectCountries,
  selectCountriesError,
  selectCountriesLoading,
  selectDuplicatedClientError,
  selectLoadedClient,
  selectLoadedClientError,
  selectLoadedClientLoading,
  selectStatesForAddress1,
  selectStatesForAddress2,
  selectUpdatedClientError,
} from '../../../@store/clients/clients.selectors';
import { Client, ClientPayload, Country } from '../../../@store/clients/clients.state.interface';
import { CONFIG } from '../../../config';

@Component({
  selector: 'ciwc-edit-client',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSelectModule,
  ],
  templateUrl: './edit-client.component.html',
  styleUrls: ['./edit-client.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditClientComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);

  readonly updatedClientError = this.store.selectSignal(selectUpdatedClientError);
  readonly client = this.store.selectSignal(selectLoadedClient);
  readonly clientLoading = this.store.selectSignal(selectLoadedClientLoading);
  readonly clientError = this.store.selectSignal(selectLoadedClientError);
  readonly addClientError = this.store.selectSignal(selectClientError);
  readonly duplicatedClientError = this.store.selectSignal(selectDuplicatedClientError);
  readonly countries = this.store.selectSignal(selectCountries);
  readonly countriesLoading = this.store.selectSignal(selectCountriesLoading);
  readonly countriesError = this.store.selectSignal(selectCountriesError);
  readonly statesForAddress1 = this.store.selectSignal(selectStatesForAddress1);
  readonly statesForAddress2 = this.store.selectSignal(selectStatesForAddress2);
  readonly CONFIG = CONFIG;

  allCountries: Country[] = [];
  clientId = this.route.snapshot.params['id'];
  duplicatedClientData: Client | null = null;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  isDuplicate = signal(false);
  isUpdatingClient = signal(false);
  showAddress1StateInput = signal(false);
  showAddress1StateSelection = signal(false);
  showAddress2StateInput = signal(false);
  showAddress2StateSelection = signal(false);
  showOptions = signal(true);

  clientForm = this.fb.group({
    // Basic Information
    company: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    secondaryEmail: ['', [Validators.email]],
    primaryPhone: [''],
    secondaryPhone: [''],
    fax: [''],
    terms: ['', [Validators.required]],
    creditInfo: [false, [Validators.required]],

    // Address 1
    address1Type: [''],
    customerCode1: ['', [Validators.required]],
    address1Street: ['', [Validators.required]],
    address1StreetDetails: [''],
    address1City: [''],
    address1Zip: [''],
    address1Country: [''],
    address1State: [''],

    // Address 2
    address2Type: [''],
    customerCode2: [''],
    address2Street: [''],
    address2StreetDetails: [''],
    address2City: [''],
    address2Zip: [''],
    address2Country: [''],
    address2State: [''],
  });

  constructor() {
    effect(() => {
      this.allCountries = this.countries() ?? [];

      if (this.allCountries.length > 0) {
        this.listenToCountryChanges();
        this.checkIfDuplicateRoute();
      }

      if (this.updatedClientError() || this.addClientError()) {
        this.isUpdatingClient.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.loadCountries();
    this.loadClient();
  }

  onSubmit(): void {
    this.isUpdatingClient.set(true);

    if (this.clientForm.valid) {
      const formValue = this.clientForm.value;

      const payload: ClientPayload = {
        company: formValue.company || '',
        firstName: formValue.firstName || '',
        lastName: formValue.lastName || '',
        email: formValue.email || '',
        secondaryEmail: formValue.secondaryEmail || '',
        primaryPhone: formValue.primaryPhone || '',
        secondaryPhone: formValue.secondaryPhone || '',
        fax: formValue.fax || '',
        terms: formValue.terms as 'NET30' | 'COD' | 'CIA' | 'CC' | 'Other',
        creditInfo: formValue.creditInfo || false,
        address1Type: formValue.address1Type || '',
        customerCode1: formValue.customerCode1 || '',
        address1Street: formValue.address1Street || '',
        address1StreetDetails: formValue.address1StreetDetails || '',
        address1City: formValue.address1City || '',
        address1Zip: formValue.address1Zip || '',
        address1Country: formValue.address1Country || '',
        address1State: formValue.address1State || '',
        address2Type: formValue.address2Type || '',
        customerCode2: formValue.customerCode2 || '',
        address2Street: formValue.address2Street || '',
        address2StreetDetails: formValue.address2StreetDetails || '',
        address2City: formValue.address2City || '',
        address2Zip: formValue.address2Zip || '',
        address2Country: formValue.address2Country || '',
        address2State: formValue.address2State || '',
      };

      if (this.isDuplicate()) {
        this.localStorageService.removeItem('duplicatedClient');
        this.store.dispatch(ClientsApiActions.addClient({ payload }));
      } else {
        this.store.dispatch(ClientsApiActions.updateClient({ payload, clientId: this.clientId }));
      }
    }
  }

  onCancel(): void {
    this.router.navigate(['/clients']);
  }

  duplicateClient(): void {
    this.store.dispatch(ClientsApiActions.duplicateClient({ clientId: this.clientId }));
  }

  private loadCountries(): void {
    this.store.dispatch(ClientsApiActions.loadCountries({ payload: { limit: -1, sort: 'name' } }));
  }

  private loadClient(): void {
    if (this.clientId) {
      this.store.dispatch(ClientsApiActions.loadClient({ clientId: this.clientId }));
    }
  }

  private listenToCountryChanges(): void {
    // Add value change listeners for country inputs
    this.clientForm
      .get('address1Country')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        if (!value) {
          this.showAddress1StateSelection.set(false);
          this.showAddress1StateInput.set(false);

          return;
        }

        const selectedCountry = this.allCountries.find((country) => country.name === value);

        if (selectedCountry?.hasStates) {
          this.showAddress1StateSelection.set(true);
          this.showAddress1StateInput.set(false);
          this.store.dispatch(
            ClientsApiActions.loadStatesForAddress1({
              payload: { country: selectedCountry.id, limit: -1, sort: 'name' },
            }),
          );
        } else {
          this.showAddress1StateSelection.set(false);
          this.showAddress1StateInput.set(true);
        }
      });
    this.clientForm.get('address2Country')?.valueChanges.subscribe((value: string | null) => {
      if (!value) {
        this.showAddress2StateSelection.set(false);
        this.showAddress2StateInput.set(false);

        return;
      }

      const selectedCountry = this.allCountries.find((country) => country.name === value);

      if (selectedCountry?.hasStates) {
        this.showAddress2StateSelection.set(true);
        this.showAddress2StateInput.set(false);
        this.store.dispatch(
          ClientsApiActions.loadStatesForAddress2({
            payload: { country: selectedCountry.id, limit: -1, sort: 'name' },
          }),
        );
      } else {
        this.showAddress2StateSelection.set(false);
        this.showAddress2StateInput.set(true);
      }
    });
  }

  private patchClientForm(client: Client | null): void {
    if (!client) return;

    this.clientForm.patchValue({
      company: client.company,
      firstName: client.firstName,
      lastName: client.lastName,
      email: client.email,
      secondaryEmail: client.secondaryEmail,
      primaryPhone: client.primaryPhone,
      secondaryPhone: client.secondaryPhone,
      fax: client.fax,
      terms: client.terms,
      creditInfo: client.creditInfo,
      address1Type: client.address1Type,
      customerCode1: client.customerCode1,
      address1Street: client.address1Street,
      address1StreetDetails: client.address1StreetDetails,
      address1City: client.address1City,
      address1Zip: client.address1Zip,
      address1Country: client.address1Country?.name,
      address1State: client.address1State,
      address2Type: client.address2Type,
      customerCode2: client.customerCode2,
      address2Street: client.address2Street,
      address2StreetDetails: client.address2StreetDetails,
      address2City: client.address2City,
      address2Zip: client.address2Zip,
      address2Country: client.address2Country?.name,
      address2State: client.address2State,
    });
  }

  private checkIfDuplicateRoute(): void {
    this.isDuplicate.set(this.router.url.includes('/duplicate'));

    if (this.isDuplicate()) {
      this.showOptions.set(false);
      this.duplicatedClientData =
        JSON.parse(this.localStorageService.getItem('duplicatedClient')) || null;

      this.patchClientForm(this.duplicatedClientData);
    } else {
      this.showOptions.set(true);
      this.patchClientForm(this.client() || null);
    }
  }
}
